/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource, Photo as CapacitorPhoto } from '@capacitor/camera';
import { StoreName } from '../entities/StoreName';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../constant/constant';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Subject } from 'rxjs';
import { DbTransaction } from '../models/db/db-transaction';
import { Photo } from '../entities/photo';
import { DataService } from './data.service';
import { EqualFilter } from '../baseServices/filter.interface';
import { AzureBlobStorageService } from '../baseServices/azure-blob-storage.service';
import { BlobType } from '../enums/blob-storage/blob-type.enum';
import { dataUrlToBlob } from '../utils/converters/to-blob.util';
import { blobToDataUrl } from '../utils/converters/to-dataUrl.util';

@Injectable({
  providedIn: 'root',
})
export class PhotoService extends DataService {
  private emitChangeSource = new Subject<any>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor(private azureBlobStorageService: AzureBlobStorageService) {
    super();
  }

  emitChange(change: any) {
    this.emitChangeSource.next(change);
  }

  async addNewToGallery(
    source: CameraSource = CameraSource.Prompt,
    dimension?: { width: number; height: number },
    quality?: number
  ): Promise<{ blob: Blob; capturedPhoto: CapacitorPhoto; size: number }> {
    // Take a photo
    let capturedPhoto;
    await Camera.getPhoto({
      resultType: CameraResultType.DataUrl,
      source,
      quality: quality ? quality : GlobalConstants.DEFAULT_QUALITY,
      ...(dimension && { width: dimension.width, height: dimension.height }),
      correctOrientation: true,
    })
      .then(photo => (capturedPhoto = photo))
      .catch(error => {
        //user closed the camera
      });
    if (!capturedPhoto) {
      return;
    }

    const blob = dataUrlToBlob(capturedPhoto.dataUrl);
    const rawData = atob(capturedPhoto.dataUrl.split(',')[1]);

    return { blob, capturedPhoto, size: rawData.length };
  }

  async getOne(filter: any = undefined) {
    const result = await this.repository.getAll(StoreName.Photo, filter);
    return result && result.length > 0 ? result[0] : null;
  }

  async getAll(filter: any = undefined) {
    return await this.repository.getAll(StoreName.Photo, filter);
  }
  async getAllAttachment(filter: any = undefined) {
    return await this.repository.getAll(StoreName.Attachment, filter);
  }

  async getUriTokenForThumbnail(photo: any): Promise<any> {
    const url = environment.attachmentApiUrl + GlobalConstants.PHOTO_UPLOAD_URI;
    return await this.http.post(url, { FileName: photo.UniqueId, IsThumb: false }).toPromise();
  }

  async getPendingBadgePhoto() {
    const filter: EqualFilter = { equals: { field: 'isBadgePhoto', searchString: true } };

    return await this.getOne(filter);
  }

  async deleteServerAttachment(attachment: any): Promise<any> {
    const url = environment.attachmentApiUrl + GlobalConstants.DELETE_ATTACHMENT_URI + `(${attachment.Id})`;
    return await this.http.delete(url, attachment).toPromise();
  }

  async deleteAttachment(attachment: any): Promise<any> {
    await this.repository.delete(attachment, StoreName.Attachment);
  }

  async createPhoto(photo: Photo, existingTransaction?: DbTransaction): Promise<number> {
    const createdId = await this.repository.create(photo, StoreName.Photo);
    return Promise.resolve(createdId);
  }

  async createPhotoWithoutUpload(photo: Photo): Promise<number> {
    const createdId = await this.repository.create(photo, StoreName.Photo);
    return Promise.resolve(createdId);
  }

  async createAttachment(attachment: any, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const createdId = await this.repository.create(attachment, StoreName.Attachment, transaction);
    return Promise.resolve(createdId);
  }

  async updateAttachment(attachment: any): Promise<number> {
    try {
      const createdId = await this.repository.update(attachment, StoreName.Attachment);
      return Promise.resolve(createdId);
    } catch (err) {
      // console.log(err);
    }
  }

  async deletePhoto(photo: Photo) {
    await this.repository.delete(photo.RecordId, StoreName.Photo);
  }

  async storeAttachments(attachments: any[]) {
    await Promise.all(
      attachments.map(async (attachment: any) => {
        await this.storeAttachmentInLocal(attachment);
        return Promise.resolve();
      })
    );
  }

  async storeAttachmentInLocal(attachment) {
    if (attachment && !attachment.localUri) {
      let response: any;
      let base64Data = attachment.dataUrl || '';
      if (attachment.serverUri) {
        response = await fetch(attachment.serverUri).catch(async e => {
          const obj = {
            event: 'storeAttachmentInLocal',
            url: attachment.serverUri,
            requestBody: null,
            data: {
              error: e.toString(),
              status: e.status,
              statusText: e.statusText,
            },
          };
          await this.repository.create(obj, StoreName.EventLog);
        });
        if (response) {
          const blob = await response.blob();
          base64Data = await blobToDataUrl(blob);
        }
      }

      if (base64Data) {
        const savedFile = await Filesystem.writeFile({
          path: `tetratech/attachments/${attachment.UniqueId}`,
          directory: Directory.Data,
          data: base64Data,
          recursive: true,
        });

        attachment.localUri = savedFile.uri;

        await this.createAttachment(attachment);
      }
    }
    return Promise.resolve();
  }

  async uploadPhoto(fileName: string, dataUrl: string): Promise<string> {
    const blob = dataUrlToBlob(dataUrl);
    
    const namedBlob = blob as Blob & { name?: string };
    namedBlob.name = fileName;
    const file = namedBlob as File;

    const { uriToken, blobFileName } = await this.azureBlobStorageService.upload(file, BlobType.PHOTO);
    return uriToken;
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.Attachment], 'readwrite'));
  }
}
